// The sting (brand.md §11, R1655; mockup round 079, card 29 "Mark in
// motion").
//
// The sting is a canvas animation of 2.5 s: a sonar ping goes out from
// the centre, a sweep draws the edge of the sign, the chevron rises into
// the cell, and the name AQUAPHOBIA closes its tracking under it, in the
// lockup voice (the face, the weight and the tracking of `LOCKUP`). It
// draws the parts of `ui/sign.ts` and holds no geometry of its own: the
// edge is `signHexPath`, the chevron is `signGlyphPath`, and the whole
// sign is `drawSign`.
//
// It plays in three places (brand.md §11.2):
//
// - the black cover of the game page while a world loads (`ui/dockin.ts`):
//   it ends on its last frame and holds it, and it never delays the
//   arrival;
// - the first 2.5 s of each mode reel (`reel/kit.ts`, `reel/page.ts`): it
//   stands on a veil of the deep water and fades into the scene;
// - the season timelapse, before frame 0 when PLAY starts the play from
//   day 0 (`ui/timelapse/`): on the same veil, and a tap skips it.
//
// Under `prefers-reduced-motion` a place draws the last frame and no
// other. The client has no motion setting of its own.
//
// On a page of the dev identity the DEV line opens with the rule under the
// name, and the last frame keeps it (brand.md §15, R2880).
//
// `drawSting` is a pure function of the time, so a test and the stepped
// clock of the reel page read one frame for one time.

import { LOCKUP, PALETTE_TOKENS, SIGN } from '../gen/presentation';
import { deviceRatio, MAX_DPR } from '../render/dpr';
import { clamp01 } from '../render/math';
import { reducedMotion } from '../render/motion';
import { easeInOut, easeOut } from '../render/spawn-plan';
import {
  SIGN_BOX, devBrand, drawSign, signGeometry, signGlyphPath, signHexPath,
  type SignContext,
} from './sign';
import { chromeAccent, signTint } from './tabicon';
import { rgbOfHex } from './theme';

/** The length of the sting, in seconds. */
export const STING_SEC = 2.5;
/** The last frame: the whole sign and the name, before a fade starts. A
 *  place that holds the sting, and every place under reduced motion,
 *  shows this frame. */
export const STING_LAST_SEC = 2.2;

/**
 * The proportions of card 29. They are the picture of the card and not
 * figures a designer tunes, so they stay here, beside the code that spends
 * them (the rule of `render/effect-recipe-kit.ts`). Every time is in
 * seconds from the start of the sting, and every length is in `u`, the
 * unit of the frame: the smaller of a sixteenth of its width and a ninth
 * of its height.
 */
const P = {
  /** The side of the sign box, in u, and the height of its centre, as a
   *  share of the frame height. */
  sign: 4.2,
  centreY: 0.42,
  /** The two rings of the ping: the start and the alpha. Each ring runs
   *  for `pingSec` out to `pingReach` of the sign box. */
  pings: [[0, 0.9], [0.22, 0.45]] as const,
  pingSec: 0.8,
  pingFrom: 0.06,
  pingReach: 0.8,
  pingWidth: 0.14,
  /** The sweep that draws the edge, and the six rays that trail it. */
  sweepAt: 0.15,
  sweepSec: 0.8,
  rays: 6,
  rayStep: 0.05,
  rayAlpha: 0.55,
  rayReach: 0.56,
  rayWidth: 0.05,
  /** The rise of the chevron: the start, the length, the drop it rises
   *  from (of the sign box) and how fast it shows. */
  riseAt: 0.7,
  riseSec: 0.55,
  riseDrop: 0.24,
  riseShow: 2.5,
  /** The name: the start, the size (of u), the gap under the sign box (of
   *  u), the tracking it closes from (em), how long the tracking closes,
   *  the stagger of the letters and how long each one fades in. */
  nameAt: 1.0,
  nameSize: 0.62,
  nameGap: 1.25,
  trackFrom: 1.1,
  trackSec: 0.75,
  letterStep: 0.05,
  letterSec: 0.3,
  /** The rule under the name: the start, the length, its width and its
   *  gap under the base line (of u), its alpha. */
  ruleAt: 1.45,
  ruleSec: 0.4,
  ruleWidth: 1.6,
  ruleGap: 0.5,
  ruleHeight: 0.05,
  ruleAlpha: 0.7,
  /** The fade of a sting that does not hold, and the veil under it: its
   *  alpha and how fast it comes in. */
  fadeAt: 2.3,
  fadeSec: 0.2,
  veil: 0.74,
  veilIn: 0.2,
} as const;

/** The name the sting writes. It is the name of the game, as the lockup
 *  writes it, and no sentence of a language. */
export const STING_NAME = 'AQUAPHOBIA';

/** How a place draws the sting. */
export interface StingStyle {
  /** The ink of the edge, the ping and the rule, `#rrggbb`. */
  accent: string;
  /** The ink of the chevron and the name. */
  bright: string;
  /** True: a veil of the deep water stands under the sting, over what the
   *  canvas covers (a reel, the map of the season timelapse). False: the
   *  canvas is clear around the sting (the black cover). */
  veil: boolean;
  /** `hold`: the sting ends on its last frame and keeps it. `fade`: the
   *  sting and its veil fade out at the end, and the frame is clear at
   *  `STING_SEC`. */
  end: 'hold' | 'fade';
  /** True: the DEV line of the dev identity stands under the rule
   *  (brand.md §15). Absent is prod. */
  dev?: boolean;
}

/** The style of a sting in the accent the chrome wears, with the bright
 *  of that accent (brand.md §1.5), and the DEV line on a page of the dev
 *  identity (brand.md §15). */
export function chromeStingStyle(veil: boolean, end: StingStyle['end']): StingStyle {
  const accent = chromeAccent();
  return { accent, bright: signTint(accent), veil, end, dev: devBrand() };
}

function backOut(v: number): number { const a = 1.5; return 1 + (a + 1) * (v - 1) ** 3 + a * (v - 1) ** 2; }

/** What the sting shows at one time: every part as a share, 0 to 1. */
export interface StingParts {
  /** The radius of each ring of the ping as a share of the sign box, its
   *  alpha and the share of its width; a ring that is not out has alpha 0. */
  pings: { r: number; alpha: number; width: number }[];
  /** How much of the edge the sweep has drawn. */
  sweep: number;
  /** The rise of the chevron: 0 not yet, 1 in place. Its offset is
   *  `drop` of the sign box, and its alpha `show`. */
  rise: number;
  drop: number;
  show: number;
  /** The alpha of each letter of the name, and the tracking in em. */
  letters: number[];
  track: number;
  /** How much of the rule stands. */
  rule: number;
  /** The alpha of the whole sting, and of its veil. */
  fade: number;
  veil: number;
}

/** The parts of the sting at `t` seconds, for one end. Pure. */
export function stingParts(t: number, end: StingStyle['end'] = 'fade'): StingParts {
  const s = end === 'hold' ? Math.min(Math.max(0, t), STING_LAST_SEC) : Math.max(0, t);
  const fade = end === 'hold' ? 1 : 1 - clamp01((s - P.fadeAt) / P.fadeSec);
  const pings = P.pings.map(([at, alpha]) => {
    const q = clamp01((s - at) / P.pingSec);
    return q <= 0 || q >= 1 ? { r: 0, alpha: 0, width: 0 }
      : { r: P.pingFrom + P.pingReach * easeOut(q), alpha: alpha * (1 - q), width: 1 - q };
  });
  const rise = clamp01((s - P.riseAt) / P.riseSec);
  const letters: number[] = [];
  for (let i = 0; i < STING_NAME.length; i++) letters.push(clamp01((s - P.nameAt - i * P.letterStep) / P.letterSec));
  return {
    pings,
    sweep: easeInOut(clamp01((s - P.sweepAt) / P.sweepSec)),
    rise,
    drop: rise > 0 ? (1 - backOut(rise)) * P.riseDrop : P.riseDrop,
    show: clamp01(rise * P.riseShow),
    letters,
    track: P.trackFrom - (P.trackFrom - LOCKUP.trackEm) * easeOut(clamp01((s - P.nameAt) / P.trackSec)),
    rule: easeOut(clamp01((s - P.ruleAt) / P.ruleSec)),
    fade,
    veil: P.veil * clamp01(s / P.veilIn) * fade,
  };
}

/** `#rrggbb` at an alpha, as a canvas takes it. */
function ink(hex: string, a: number): string {
  const [r, g, b] = rgbOfHex(hex);
  return `rgb(${r} ${g} ${b} / ${Math.round(a * 1000) / 1000})`;
}

/** The deep water of the veil: the first paint of `--deep-rgb`, which no
 *  theme turns here (card 29 draws the frozen deep). */
const DEEP = PALETTE_TOKENS['--deep-rgb'];

/** The font of the name: the lockup face, then the chrome's fallback. */
export function stingFont(px: number): string {
  return `${LOCKUP.weight} ${Math.round(px * 100) / 100}px "${LOCKUP.family}", ui-monospace, monospace`;
}

/** The edge and the glyph of the full cut with no plate, built once. */
let parts: { hex: Path2D; glyph: Path2D; edge: number } | null = null;

function signParts(): { hex: Path2D; glyph: Path2D; edge: number } {
  if (!parts) {
    const g = signGeometry('full', 'round');
    parts = { hex: new Path2D(signHexPath(g)), glyph: new Path2D(signGlyphPath(g)), edge: g.edge };
  }
  return parts;
}

/**
 * Draws one frame of the sting at `t` seconds into a `w` x `h` frame, in
 * the context's own units. The frame is cleared first, so the canvas holds
 * the sting and its veil and nothing else.
 */
export function drawSting(ctx: SignContext, w: number, h: number, t: number, style: StingStyle): void {
  const p = stingParts(t, style.end);
  ctx.clearRect(0, 0, w, h);
  if (style.veil && p.veil > 0) {
    ctx.fillStyle = `rgb(${DEEP} / ${Math.round(p.veil * 1000) / 1000})`;
    ctx.fillRect(0, 0, w, h);
  }
  if (p.fade <= 0) return;
  const u = Math.min(w / 16, h / 9);
  const G = u * P.sign;
  const cx = w / 2;
  const cy = h * P.centreY;
  ctx.save();
  ctx.globalAlpha = p.fade;

  // The ping: rings out from the centre.
  for (const ring of p.pings) {
    if (ring.alpha <= 0) continue;
    ctx.strokeStyle = ink(style.accent, ring.alpha);
    ctx.lineWidth = Math.max(0.75, u * P.pingWidth * ring.width);
    ctx.beginPath();
    ctx.arc(cx, cy, G * ring.r, 0, Math.PI * 2);
    ctx.stroke();
  }

  const box = { x: cx - G / 2, y: cy - G / 2 };
  if (p.sweep >= 1 && p.rise >= 1) {
    drawSign(ctx, box.x, box.y, G, { plate: false, accent: style.accent, bright: style.bright });
  } else {
    const sp = signParts();
    const k = G / SIGN_BOX;
    if (p.sweep > 0) {
      // The edge draws on behind the sweep, from the top clockwise.
      const a0 = -Math.PI / 2;
      const a1 = a0 + p.sweep * Math.PI * 2;
      ctx.save();
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.arc(cx, cy, G, a0, a1);
      ctx.closePath();
      ctx.clip();
      ctx.translate(box.x, box.y);
      ctx.scale(k, k);
      ctx.lineJoin = 'round';
      ctx.lineWidth = sp.edge;
      ctx.strokeStyle = style.accent;
      ctx.stroke(sp.hex);
      ctx.restore();
      if (p.sweep < 1) {
        ctx.lineWidth = Math.max(0.75, u * P.rayWidth);
        for (let i = 0; i < P.rays; i++) {
          const a = a1 - i * P.rayStep;
          ctx.strokeStyle = ink(style.bright, P.rayAlpha * (1 - i / P.rays));
          ctx.beginPath();
          ctx.moveTo(cx, cy);
          ctx.lineTo(cx + Math.cos(a) * G * P.rayReach, cy + Math.sin(a) * G * P.rayReach);
          ctx.stroke();
        }
      }
    }
    if (p.show > 0) {
      // The chevron rises into the cell.
      ctx.save();
      ctx.globalAlpha = p.fade * p.show;
      ctx.translate(box.x, box.y + p.drop * G);
      ctx.scale(k, k);
      ctx.fillStyle = style.bright;
      ctx.fill(sp.glyph, 'evenodd');
      ctx.restore();
    }
  }

  // The name, letter by letter, as the tracking closes. No tracking stands
  // after the last letter (brand.md §2.1), so the name centres on its ink.
  const size = u * P.nameSize;
  const base = cy + G / 2 + u * P.nameGap;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';
  ctx.fillStyle = style.bright;
  drawSpaced(ctx, STING_NAME, cx, base, size, p.track * size, p.fade, p.letters);
  if (p.rule > 0) {
    ctx.globalAlpha = p.fade;
    ctx.fillStyle = ink(style.accent, P.ruleAlpha);
    const rw = u * P.ruleWidth * p.rule;
    ctx.fillRect(cx - rw / 2, base + u * P.ruleGap, rw, Math.max(1, u * P.ruleHeight));
    if (style.dev) drawDevLine(ctx, cx, base + u * LOCKUP.dev.stingDrop, u * LOCKUP.dev.stingCap, p.fade * p.rule);
  }
  ctx.restore();
}

/** The DEV line of the dev identity (brand.md §15): the word in the
 *  lockup face at the cap height `cap`, centred on `cx` with its base line
 *  at `base`, in amber at the alpha `a`. It opens with the rule. */
function drawDevLine(ctx: SignContext, cx: number, base: number, cap: number, a: number): void {
  const size = cap / LOCKUP.faceCapEm;
  ctx.fillStyle = SIGN.dev.ink;
  drawSpaced(ctx, LOCKUP.dev.word, cx, base, size, LOCKUP.dev.trackEm * size, a);
}

/** One word in the lockup face at `size` px, letter by letter with `gap`
 *  px between two letters, centred on `cx` with its base line at `base`.
 *  The face is monospaced, so every letter takes the width of `M`. Letter
 *  i stands at the alpha `alpha × letters[i]`, and a letter at 0 is not
 *  drawn; with no `letters` every letter stands at `alpha`. */
function drawSpaced(
  ctx: SignContext, word: string, cx: number, base: number, size: number, gap: number,
  alpha: number, letters?: readonly number[],
): void {
  ctx.font = stingFont(size);
  const cw = ctx.measureText('M').width;
  let x = cx - (word.length * cw + (word.length - 1) * gap) / 2;
  for (let i = 0; i < word.length; i++) {
    const a = letters ? letters[i] : 1;
    if (a > 0) {
      ctx.globalAlpha = alpha * a;
      ctx.fillText(word[i], x, base);
    }
    x += cw + gap;
  }
}

// ---------------------------------------------------------------- the face

/** The file of the lockup face, as the stylesheets declare it
 *  (`ui/styles.css.tmpl`, `page.css.tmpl`). A page that declares no face
 *  of `LOCKUP.family` (the reel page) gets it here. */
const LOCKUP_FACE_URL = '/fonts/jetbrains-mono-500-chart.woff2';

let faceLoad: Promise<void> | null = null;

/**
 * Loads the lockup face for the canvas. A canvas takes a face only once it
 * has loaded, and a stylesheet loads a face only when an element asks for
 * it, so the sting asks. The promise never rejects: the fallback face
 * draws the name until the face lands.
 */
function loadStingFace(): Promise<void> {
  if (faceLoad) return faceLoad;
  faceLoad = (async () => {
    try {
      const fonts = typeof document !== 'undefined' ? document.fonts : undefined;
      if (!fonts) return;
      const declared = [...fonts].some((f) => f.family.replace(/["']/g, '') === LOCKUP.family);
      if (!declared && typeof FontFace === 'function') {
        const face = new FontFace(LOCKUP.family, `url(${LOCKUP_FACE_URL}) format("woff2")`, {
          weight: String(LOCKUP.weight), style: 'normal',
        });
        fonts.add(face);
      }
      await fonts.load(stingFont(16));
    } catch {
      // The fallback face draws the name.
    }
  })();
  return faceLoad;
}

// ---------------------------------------------------------------- the layer

/**
 * One canvas that shows the sting over its host. The host places it: the
 * canvas fills the host's box (`position: absolute; inset: 0`). A tap on it
 * calls `onTap`, and a layer with no `onTap` takes no pointer.
 */
export class StingLayer {
  readonly el: HTMLCanvasElement;

  constructor(private readonly style: StingStyle, onTap?: () => void) {
    const c = document.createElement('canvas');
    c.className = 'sting';
    c.setAttribute('aria-hidden', 'true');
    c.style.cssText = 'position:absolute;left:0;top:0;width:100%;height:100%;display:block;'
      + `pointer-events:${onTap ? 'auto' : 'none'}`;
    if (onTap) {
      c.addEventListener('pointerdown', (ev) => {
        if (ev.button > 0) return;
        ev.preventDefault();
        ev.stopPropagation();
        onTap();
      });
    }
    this.el = c;
    void loadStingFace();
  }

  /** Draws the frame at `t` seconds at the laid-out size of the canvas. */
  draw(t: number): void {
    const c = this.el;
    const w = c.clientWidth;
    const h = c.clientHeight;
    if (w <= 0 || h <= 0) return;
    const dpr = deviceRatio(MAX_DPR);
    const bw = Math.max(1, Math.round(w * dpr));
    const bh = Math.max(1, Math.round(h * dpr));
    if (c.width !== bw || c.height !== bh) {
      c.width = bw;
      c.height = bh;
    }
    const ctx = c.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(bw / w, 0, 0, bh / h, 0, 0);
    drawSting(ctx, w, h, t, this.style);
  }

  /** Takes the canvas out of the document. */
  remove(): void { this.el.remove(); }
}

/** What runs a sting on the wall clock: the animation frame and a clock in
 *  ms. A test passes its own. */
export interface StingClock {
  request(fn: (ms: number) => void): number;
  cancel(id: number): void;
  now(): number;
}

function pageClock(): StingClock {
  if (typeof requestAnimationFrame !== 'function') {
    return {
      request: (fn) => setTimeout(() => fn(performance.now()), 16) as unknown as number,
      cancel: (id) => clearTimeout(id),
      now: () => performance.now(),
    };
  }
  return {
    request: (fn) => requestAnimationFrame(fn),
    cancel: (id) => cancelAnimationFrame(id),
    now: () => performance.now(),
  };
}

/**
 * Plays a layer on the wall clock from now. A `hold` sting stops its clock
 * on the last frame and keeps it; a `fade` sting stops at `STING_SEC` and
 * calls `onEnd`. Under reduced motion it draws the last frame once, and a
 * `fade` sting ends after `STING_SEC` on a timer. A resize draws the
 * frame again. The answer stops it; it never removes the layer.
 */
export function playSting(
  layer: StingLayer, style: StingStyle, onEnd?: () => void,
  clock: StingClock = pageClock(), still: boolean = reducedMotion(),
): () => void {
  let raf = 0;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let stopped = false;
  let shown = 0;
  const start = clock.now();
  const onResize = (): void => layer.draw(shown);
  const stop = (): void => {
    if (stopped) return;
    stopped = true;
    if (raf !== 0) clock.cancel(raf);
    raf = 0;
    if (timer !== null) clearTimeout(timer);
    timer = null;
    if (typeof window !== 'undefined') window.removeEventListener('resize', onResize);
  };
  if (typeof window !== 'undefined') window.addEventListener('resize', onResize);
  if (still) {
    shown = STING_LAST_SEC;
    layer.draw(shown);
    if (style.end === 'fade') timer = setTimeout(() => { stop(); onEnd?.(); }, STING_SEC * 1000);
    return stop;
  }
  const frame = (): void => {
    raf = 0;
    if (stopped) return;
    const t = (clock.now() - start) / 1000;
    if (style.end === 'hold' && t >= STING_LAST_SEC) {
      // The last frame stands; the clock stops, and a resize redraws it.
      shown = STING_LAST_SEC;
      layer.draw(shown);
      return;
    }
    if (style.end === 'fade' && t >= STING_SEC) {
      shown = STING_SEC;
      layer.draw(shown);
      stop();
      onEnd?.();
      return;
    }
    shown = t;
    layer.draw(t);
    raf = clock.request(frame);
  };
  frame();
  return stop;
}
